import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ExternalLink, Plus, Trash2, ChevronUp, ChevronDown, Upload } from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { InlineConfirm } from '@/components/ui/InlineConfirm';
import { Field, Input, Select, Textarea } from '@/components/ui/Field';
import { Spinner, EmptyState } from '@/components/ui/EmptyState';
import { useToast } from '@/components/ui/ToastContext';
import { supabase } from '@/lib/supabaseClient';
import { uploadPageImage, slugify } from '@/lib/customPageApi';
import type { Article, BodyBlock } from '@/types/article';
import { rxkModuleIcon, rxkModuleTitle, rxkNavGroups } from '../../nav';

const CATEGORIES = ['Skincare Tips', 'Before & After', 'Events', 'Brand News'];

function defaultBlock(type: BodyBlock['type']): BodyBlock {
  switch (type) {
    case 'paragraph':
      return { type: 'paragraph', content: '' };
    case 'heading':
      return { type: 'heading', content: 'New heading' };
    case 'subheading':
      return { type: 'subheading', content: 'New subheading' };
    case 'list':
      return { type: 'list', items: ['First item'] };
    case 'blockquote':
      return { type: 'blockquote', content: '' };
  }
}

export default function AdminArticleDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { push } = useToast();

  const [article, setArticle] = useState<Article | null>(null);
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [category, setCategory] = useState('Skincare Tips');
  const [categoryColor, setCategoryColor] = useState('#7B1113');
  const [readTime, setReadTime] = useState('');
  const [excerpt, setExcerpt] = useState('');
  const [author, setAuthor] = useState('');
  const [authorRole, setAuthorRole] = useState('');
  const [publishedDate, setPublishedDate] = useState('');
  const [image, setImage] = useState('');
  const [status, setStatus] = useState<'draft' | 'published'>('draft');
  const [body, setBody] = useState<BodyBlock[]>([]);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    const { data } = await supabase
      .from('rxk_articles')
      .select('id, slug, category, category_color, read_time, title, excerpt, author, author_role, published_date, image, body, status')
      .eq('id', id)
      .maybeSingle();
    const a = data as Article | null;
    setArticle(a);
    if (a) {
      setTitle(a.title);
      setSlug(a.slug);
      setCategory(a.category);
      setCategoryColor(a.category_color);
      setReadTime(a.read_time ?? '');
      setExcerpt(a.excerpt ?? '');
      setAuthor(a.author ?? '');
      setAuthorRole(a.author_role ?? '');
      setPublishedDate(a.published_date);
      setImage(a.image ?? '');
      setStatus(a.status);
      setBody(a.body ?? []);
    }
    setLoading(false);
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const updateBlock = (index: number, patch: Partial<BodyBlock>) => {
    setBody((prev) => prev.map((b, i) => (i === index ? ({ ...b, ...patch } as BodyBlock) : b)));
  };

  const removeBlock = (index: number) => setBody((prev) => prev.filter((_, i) => i !== index));

  const moveBlock = (index: number, dir: -1 | 1) => {
    setBody((prev) => {
      const next = [...prev];
      const target = index + dir;
      if (target < 0 || target >= next.length) return prev;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const addBlock = (type: BodyBlock['type']) => setBody((prev) => [...prev, defaultBlock(type)]);

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploadingImage(true);
    try {
      const url = await uploadPageImage(file);
      setImage(url);
    } catch (err) {
      push('error', err instanceof Error ? err.message : 'Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSave = async () => {
    if (!id) return;
    if (!slug.trim()) {
      push('error', 'Slug is required');
      return;
    }
    setSaving(true);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      const { error } = await supabase
        .from('rxk_articles')
        .update({
          title,
          slug: slugify(slug),
          category,
          category_color: categoryColor,
          read_time: readTime || null,
          excerpt: excerpt || null,
          author: author || null,
          author_role: authorRole || null,
          published_date: publishedDate,
          image: image || null,
          status,
          body,
          updated_by: user?.id ?? null,
        })
        .eq('id', id);
      if (error) throw error;
      push('success', status === 'published' ? 'Article saved and live' : 'Draft saved');
      await load();
    } catch (err) {
      push('error', err instanceof Error ? err.message : 'Failed to save article');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    const { error } = await supabase.from('rxk_articles').delete().eq('id', id);
    if (error) {
      push('error', error.message);
      return;
    }
    push('success', 'Article deleted');
    navigate('/admin/articles');
  };

  if (loading) {
    return (
      <AppShell moduleTitle={rxkModuleTitle} moduleIcon={rxkModuleIcon} navGroups={rxkNavGroups}>
        <Spinner />
      </AppShell>
    );
  }

  if (!article) {
    return (
      <AppShell moduleTitle={rxkModuleTitle} moduleIcon={rxkModuleIcon} navGroups={rxkNavGroups}>
        <EmptyState title="Article not found" />
      </AppShell>
    );
  }

  return (
    <AppShell moduleTitle={rxkModuleTitle} moduleIcon={rxkModuleIcon} navGroups={rxkNavGroups}>
      <div className="space-y-5">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/admin/articles')} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-surface-muted text-ink-faint cursor-pointer" aria-label="Back">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <h1 className="text-[20px] font-bold text-ink flex-1 truncate">{title || 'Untitled Article'}</h1>
          <Badge tone={status === 'published' ? 'success' : 'neutral'}>{status}</Badge>
          {status === 'published' && (
            <a href={`/content/${article.slug}`} target="_blank" rel="noopener noreferrer" className="text-ink-faint hover:text-ink">
              <ExternalLink className="w-4 h-4" />
            </a>
          )}
          <InlineConfirm label="Delete" confirmLabel="Confirm delete" onConfirm={handleDelete} />
        </div>

        <Card>
          <CardHeader title="Article Settings" />
          <CardBody className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Title">
                <Input value={title} onChange={(e) => setTitle(e.target.value)} />
              </Field>
              <Field label="URL slug (renders at /content/slug)">
                <Input value={slug} onChange={(e) => setSlug(e.target.value)} />
              </Field>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Field label="Category">
                <Select value={category} onChange={(e) => setCategory(e.target.value)}>
                  {CATEGORIES.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Category color">
                <Input type="text" value={categoryColor} onChange={(e) => setCategoryColor(e.target.value)} placeholder="#7B1113" />
              </Field>
              <Field label="Read time">
                <Input value={readTime} onChange={(e) => setReadTime(e.target.value)} placeholder="4 min read" />
              </Field>
            </div>
            <Field label="Excerpt">
              <Textarea value={excerpt} onChange={(e) => setExcerpt(e.target.value)} rows={2} />
            </Field>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Field label="Author">
                <Input value={author} onChange={(e) => setAuthor(e.target.value)} />
              </Field>
              <Field label="Author role">
                <Input value={authorRole} onChange={(e) => setAuthorRole(e.target.value)} placeholder="Head Pharmacist" />
              </Field>
              <Field label="Published date">
                <Input type="date" value={publishedDate} onChange={(e) => setPublishedDate(e.target.value)} />
              </Field>
            </div>
            <Field label="Hero image">
              <div className="flex items-center gap-3">
                {image && <img src={image} alt="" className="h-16 w-24 object-cover rounded-lg" />}
                <input ref={fileInputRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" onChange={handleImageUpload} />
                <Button type="button" size="sm" variant="secondary" onClick={() => fileInputRef.current?.click()} disabled={uploadingImage}>
                  <Upload className="w-3.5 h-3.5" /> {uploadingImage ? 'Uploading…' : image ? 'Replace' : 'Upload'}
                </Button>
              </div>
              <p className="text-[10.5px] text-ink-faint mt-1.5">Wide photo, 16:9 or wider (e.g. 1600×900px) — will be cropped to fill, keep the subject centered</p>
            </Field>
            <Field label="Status">
              <Select value={status} onChange={(e) => setStatus(e.target.value as 'draft' | 'published')} className="!w-48">
                <option value="draft">Draft (not visible)</option>
                <option value="published">Published (live)</option>
              </Select>
            </Field>
          </CardBody>
        </Card>

        <Card>
          <CardHeader
            title="Body"
            subtitle={`${body.length} block(s)`}
            action={
              <div className="flex items-center gap-1.5 flex-wrap">
                <Button size="sm" variant="secondary" onClick={() => addBlock('heading')}>
                  <Plus className="w-3 h-3" /> Heading
                </Button>
                <Button size="sm" variant="secondary" onClick={() => addBlock('subheading')}>
                  <Plus className="w-3 h-3" /> Subheading
                </Button>
                <Button size="sm" variant="secondary" onClick={() => addBlock('paragraph')}>
                  <Plus className="w-3 h-3" /> Paragraph
                </Button>
                <Button size="sm" variant="secondary" onClick={() => addBlock('list')}>
                  <Plus className="w-3 h-3" /> List
                </Button>
                <Button size="sm" variant="secondary" onClick={() => addBlock('blockquote')}>
                  <Plus className="w-3 h-3" /> Quote
                </Button>
              </div>
            }
          />
          <CardBody className="space-y-3">
            {body.length === 0 ? (
              <EmptyState title="No blocks yet — add one above" />
            ) : (
              body.map((block, index) => (
                <div key={index} className="border border-border-subtle rounded-lg p-3 flex gap-3">
                  <div className="flex-1 space-y-2">
                    <Badge tone="neutral">{block.type}</Badge>
                    {(block.type === 'heading' || block.type === 'subheading' || block.type === 'blockquote') && (
                      <Input value={block.content} onChange={(e) => updateBlock(index, { content: e.target.value })} />
                    )}
                    {block.type === 'paragraph' && (
                      <Textarea
                        value={block.content}
                        onChange={(e) => updateBlock(index, { content: e.target.value })}
                        rows={3}
                        placeholder="Use **text** for bold"
                      />
                    )}
                    {block.type === 'list' && (
                      <Textarea
                        value={block.items.join('\n')}
                        onChange={(e) => updateBlock(index, { items: e.target.value.split('\n') })}
                        rows={4}
                        placeholder={'One item per line. Use **text** for bold.'}
                      />
                    )}
                  </div>
                  <div className="flex flex-col gap-1">
                    <button onClick={() => moveBlock(index, -1)} disabled={index === 0} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-surface-muted text-ink-faint disabled:opacity-30 cursor-pointer">
                      <ChevronUp className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => moveBlock(index, 1)} disabled={index === body.length - 1} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-surface-muted text-ink-faint disabled:opacity-30 cursor-pointer">
                      <ChevronDown className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => removeBlock(index)} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-error-bg text-error cursor-pointer">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </CardBody>
        </Card>

        <Button onClick={handleSave} loading={saving}>
          Save
        </Button>
      </div>
    </AppShell>
  );
}
